import {FormProvider, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import Button from 'components/Button';
import FieldResult from 'components/Form/FieldResult';
import FormHeader from 'components/Form/FormHeader';
import NavigationTabs from 'components/Navigation/NavigationTabs';
import TableStatic from 'components/Table/TableStatic';
import TooltipWrapper from 'components/Tooltip';
import {useBackend} from 'state/backend';
import {onApiKeyError, onApiKeySuccess} from 'utils/request/result';
import {usePostLicensingToggleLicense} from './services';
import {Employee, LicensingBackend} from './types';

const Licensing = () => {
    const methods = useForm();

    const {t} = useTranslation('Licensing');

    const {buttonProps, mutateAsync} = usePostLicensingToggleLicense();

    const {
        data: {currentLicenseLimit, employees, navigationTabs, usedLicenses},
        refreshData,
    } = useBackend<LicensingBackend>();

    const LICENSE_TYPE = {
        FREE: 'free',
        FULL: 'full',
    };

    const onClick = async (mid: string) => {
        methods.clearErrors(`root.${mid}`);
        await mutateAsync({mid, requestId: mid})
            .then((response) => {
                refreshData('/licensing/');
                onApiKeySuccess(methods, mid)(response);
            })
            .catch((error) => {
                onApiKeyError(methods, mid)(error);
            });
    };

    return (
        <FormProvider {...methods}>
            {employees.map((employee: Employee) => (
                <FieldResult key={employee.mid} name={employee.mid} />
            ))}
            <NavigationTabs.Team
                className="mt-2"
                selection="members"
                {...navigationTabs}
            />
            <FormHeader title={t('distributeLicenses')} />
            <dl className="my-2 w-full">
                <dt className="mb-2 inline-block w-1/5 border-r border-grey-400 py-1 pr-2 text-right text-grey-600">
                    {t('usedLicenses')}
                </dt>
                <dd className="mb-2 inline-block w-4/5 py-1 pl-2 text-left">
                    {usedLicenses}
                </dd>
                <br />
                <dt className="clear-both mb-2 inline-block w-1/5 border-r border-grey-400 py-1 pr-2 text-right text-grey-600">
                    {t('currentLicenseLimit')}
                </dt>
                <dd className="mb-2 inline-block w-4/5 py-1 pl-2 text-left">
                    {currentLicenseLimit || t('Unlimited')}
                </dd>
            </dl>
            <TableStatic>
                <TableStatic.Header>
                    <TableStatic.HeaderRow>
                        <TableStatic.Column className="w-2/5">
                            {t('memberName')}
                        </TableStatic.Column>
                        <TableStatic.Column className="w-2/5">
                            {t('userLicense')}
                        </TableStatic.Column>
                        <TableStatic.Column className="w-1/5">
                            &nbsp;
                        </TableStatic.Column>
                    </TableStatic.HeaderRow>
                </TableStatic.Header>
                <TableStatic.Body>
                    {employees.map((employee: Employee) => (
                        <TableStatic.Row key={employee.mid}>
                            <TableStatic.Cell className="w-2/5">
                                {employee.name}
                            </TableStatic.Cell>
                            <TableStatic.Cell className="w-2/5">
                                {employee.licenseType === LICENSE_TYPE.FULL
                                    ? t('licenseTypeFull')
                                    : t('licenseTypeFree')}
                            </TableStatic.Cell>
                            <TableStatic.Cell className="w-1/5">
                                <TooltipWrapper
                                    message={
                                        employee.isOwner
                                            ? t('cannotRemoveOwnerLicense')
                                            : ''
                                    }
                                >
                                    <Button
                                        className="float-right p-2"
                                        disabled={
                                            employee.isOwner ||
                                            buttonProps[employee.mid]?.disabled
                                        }
                                        id={`button-${employee.mid}`}
                                        isLoading={
                                            buttonProps[employee.mid]?.isLoading
                                        }
                                        kind="secondary"
                                        onClick={() => onClick(employee.mid)}
                                        size="xs"
                                    >
                                        {employee.licenseType ===
                                        LICENSE_TYPE.FULL
                                            ? t('changeToFreeLicense')
                                            : t('changeToFullLicense')}
                                    </Button>
                                </TooltipWrapper>
                            </TableStatic.Cell>
                        </TableStatic.Row>
                    ))}
                </TableStatic.Body>
            </TableStatic>
        </FormProvider>
    );
};

export default Licensing;
